import clsx from 'clsx';
import { useState } from 'react';

export type DetailsMode = 'internal' | 'external';

interface Props {
  mode: DetailsMode;
  disabled?: boolean;
  /** When true, switching away from the current mode asks for confirmation first,
   * since the saved details for the current mode will be cleared. */
  confirmOnSwitch: boolean;
  onSwitch: (mode: DetailsMode) => void;
}

const LABELS: Record<DetailsMode, string> = {
  internal: 'TenantPay account number',
  external: 'Landlord details',
};

const CONFIRM_COPY: Record<DetailsMode, string> = {
  internal:
    'Your landlord details will be removed when you save this home with a TenantPay account number.',
  external:
    'Your TenantPay account number will be removed when you save your landlord details.',
};

/**
 * Lets the tenant switch a building between internal (TenantPay account number)
 * and external (landlord/property manager) payment details. A building only ever
 * has one of the two, so switching clears the other on save.
 */
const DetailsModeSwitch = ({ mode, disabled, confirmOnSwitch, onSwitch }: Props) => {
  const [pendingMode, setPendingMode] = useState<DetailsMode | null>(null);

  const handleClick = (target: DetailsMode) => {
    if (disabled || target === mode) return;
    if (confirmOnSwitch) {
      setPendingMode(target);
      return;
    }
    onSwitch(target);
  };

  return (
    <>
      <div className='text-[12px] text-[#6B7285] mb-1.5'>How do you pay your landlord?</div>
      <div
        role='radiogroup'
        className='inline-flex w-full rounded-[10px] border border-[#E8EAF0] bg-[#F6F8FC] p-1 gap-1'
      >
        {(['internal', 'external'] as DetailsMode[]).map((m) => (
          <button
            key={m}
            type='button'
            role='radio'
            aria-checked={mode === m}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              handleClick(m);
            }}
            className={clsx(
              'flex-1 rounded-[8px] px-3 py-2 text-[13px] font-semibold transition-colors',
              mode === m
                ? 'bg-white text-[#0055FF] shadow-sm border border-[#0055FF]'
                : 'text-[#6B7285] border border-transparent hover:text-[#0A0B14]',
              disabled && 'opacity-60 cursor-not-allowed'
            )}
          >
            {LABELS[m]}
            {mode === m && <span className='ml-1 text-[11px] font-medium'>(current)</span>}
          </button>
        ))}
      </div>

      {pendingMode && (
        <div
          className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-4'
          onClick={() => setPendingMode(null)}
        >
          <div
            role='dialog'
            aria-modal='true'
            className='w-full max-w-sm rounded-[16px] bg-white p-5 shadow-xl'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='text-[16px] font-semibold text-[#0A0B14]'>
              Switch to {LABELS[pendingMode].toLowerCase()}?
            </div>
            <p className='mt-2 text-[13px] text-[#6B7285]'>
              {CONFIRM_COPY[pendingMode]} A home can only have one of these at a time.
            </p>
            <div className='mt-5 flex justify-end gap-2'>
              <button
                type='button'
                onClick={() => setPendingMode(null)}
                className='px-3.5 py-2 rounded-[10px] border border-[#E8EAF0] bg-white text-[13px] font-semibold text-[#6B7285]'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={() => {
                  const target = pendingMode;
                  setPendingMode(null);
                  onSwitch(target);
                }}
                className='px-3.5 py-2 rounded-[10px] bg-[#0055FF] text-white text-[13px] font-semibold hover:bg-[#0044CC]'
              >
                Switch
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DetailsModeSwitch;
